/** Friendly names of audit log actions (the backend's `AuditRepository.record` actions), as message keys of the team
 * namespace. Every action the backend records has one here (auditLabels.test.ts checks the source); an unknown action
 * gets a readable fallback. */
import type { Translate } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { localeInfo } from "../i18n/negotiate.ts";

export const AUDIT_LABELS: Record<string, MessageKey> = {
  "member.role": "team.audit.action.member.role",
  "member.status": "team.audit.action.member.status",
  "member.remove": "team.audit.action.member.remove",
  "member.leave": "team.audit.action.member.leave",
  "invitation.create": "team.audit.action.invitation.create",
  "invitation.resend": "team.audit.action.invitation.resend",
  "invitation.revoke": "team.audit.action.invitation.revoke",
  "invitation.accept": "team.audit.action.invitation.accept",
  "quote.resend": "team.audit.action.quote.resend",
  "lead.locale": "team.audit.action.lead.locale",
  "quote.proposal_retry": "team.audit.action.quote.proposalRetry",
  "ownership.transfer_requested": "team.audit.action.ownership.transferRequested",
  "ownership.transferred": "team.audit.action.ownership.transferred",
  "ownership.transfer_cancelled": "team.audit.action.ownership.transferCancelled",
  "ownership.admin_set": "team.audit.action.ownership.adminSet",
  "identity.relink": "team.audit.action.identity.relink",
  "identity.sso_jit": "team.audit.action.identity.ssoJit",
  "identity.sso_required_refused": "team.audit.action.identity.ssoRequiredRefused",
  "sso.member.relinked": "team.audit.action.sso.member.relinked",
  "sso.member.migration_failed": "team.audit.action.sso.member.migrationFailed",
  "sso.certificate.expiring": "team.audit.action.sso.certificate.expiring",
  "sso.certificate.rotated": "team.audit.action.sso.certificate.rotated",
  "platform_admin.open": "team.audit.action.platformAdmin.open",
  "platform_admin.act": "team.audit.action.platformAdmin.act",
  "embed_key.create": "team.audit.action.embedKey.create",
  "embed_key.update": "team.audit.action.embedKey.update",
  "embed_key.delete": "team.audit.action.embedKey.delete",
  "brand.logo_upload": "team.audit.action.brand.logoUpload",
  "brand.update": "team.audit.action.brand.update",
  "settings.update": "team.audit.action.settings.update",
  "electricity_price.set": "team.audit.action.electricityPrice.set",
  "electricity_price.remove": "team.audit.action.electricityPrice.remove",
  "electricity_price.limit": "team.audit.action.electricityPrice.limit",
  "email_preferences.update": "team.audit.action.emailPreferences.update",
  "design.created": "team.audit.action.design.created",
  "design.setbacks_overridden": "team.audit.action.design.setbacksOverridden",
  "design.setbacks_override_cleared": "team.audit.action.design.setbacksOverrideCleared",
  "plan_set.create": "team.audit.action.planSet.create",
  "permit_site.update": "team.audit.action.permitSite.update",
  "permit_site.photo_upload": "team.audit.action.permitSite.photoUpload",
  "design.basemap_choice": "team.audit.action.design.basemapChoice",
  "design.basemap_upload": "team.audit.action.design.basemapUpload",
  "design.basemap_align": "team.audit.action.design.basemapAlign",
  "design.basemap_upload_delete": "team.audit.action.design.basemapUploadDelete",
  "permit_site.photo_delete": "team.audit.action.permitSite.photoDelete",
  "solarapp.connect": "team.audit.action.solarapp.connect",
  "solarapp.disconnect": "team.audit.action.solarapp.disconnect",
  "solarapp.submit": "team.audit.action.solarapp.submit",
  "esign.create": "team.audit.action.esign.create",
  "esign.send": "team.audit.action.esign.send",
  "esign.cancel": "team.audit.action.esign.cancel",
  "esign.resend": "team.audit.action.esign.resend",
  "esign.correct": "team.audit.action.esign.correct",
  "sld_overrides.update": "team.audit.action.sldOverrides.update",
  "permit_defaults.update": "team.audit.action.permitDefaults.update",
  "permit_equipment.create": "team.audit.action.permitEquipment.create",
  "permit_equipment.update": "team.audit.action.permitEquipment.update",
  "permit_ahj.create": "team.audit.action.permitAhj.create",
  "permit_ahj.update": "team.audit.action.permitAhj.update",
  "permit_document.upload": "team.audit.action.permitDocument.upload",
  "permit_document.delete": "team.audit.action.permitDocument.delete",
  "permit_document.company_upload": "team.audit.action.permitDocument.companyUpload",
  "permit_document.company_delete": "team.audit.action.permitDocument.companyDelete",
  "datasheet_library.add": "team.audit.action.datasheetLibrary.add",
  "datasheet_library.update": "team.audit.action.datasheetLibrary.update",
  "datasheet_library.refresh": "team.audit.action.datasheetLibrary.refresh",
  "permit_equipment.company_create": "team.audit.action.permitEquipment.companyCreate",
  "permit_equipment.company_update": "team.audit.action.permitEquipment.companyUpdate",
  "permit_equipment.datasheet_upload": "team.audit.action.permitEquipment.datasheetUpload",
  "permit_equipment.datasheet_delete": "team.audit.action.permitEquipment.datasheetDelete",
  "design_comment.created": "team.audit.action.designComment.created",
  "design_comment.replied": "team.audit.action.designComment.replied",
  "design_comment.resolved": "team.audit.action.designComment.resolved",
  "design_comment.reopened": "team.audit.action.designComment.reopened",
  "design_comment.note_updated": "team.audit.action.designComment.noteUpdated",
  "design_option.created": "team.audit.action.designOption.created",
  "design_option.renamed": "team.audit.action.designOption.renamed",
  "design_option.chosen": "team.audit.action.designOption.chosen",
  "designs.clear": "team.audit.action.designs.clear",
  "api_key.create": "team.audit.action.apiKey.create",
  "api_key.revoke": "team.audit.action.apiKey.revoke",
  "webhook_endpoint.create": "team.audit.action.webhookEndpoint.create",
  "webhook_endpoint.update": "team.audit.action.webhookEndpoint.update",
  "webhook_endpoint.delete": "team.audit.action.webhookEndpoint.delete",
  "webhook_endpoint.reveal_secret": "team.audit.action.webhookEndpoint.revealSecret",
  "webhook_endpoint.rotate_secret": "team.audit.action.webhookEndpoint.rotateSecret",
  "webhook_delivery.replay": "team.audit.action.webhookDelivery.replay",
  "wordpress.connect": "team.audit.action.wordpress.connect",
  "wordpress.exchange": "team.audit.action.wordpress.exchange",
  "lux.grant": "team.audit.action.lux.grant",
  "lux.refund": "team.audit.action.lux.refund",
  "lux.settings": "team.audit.action.lux.settings",
  "lux.seats": "team.audit.action.lux.seats",
  "lux.auto_top_up": "team.audit.action.lux.autoTopUp",
  "payment.paid": "team.audit.action.payment.paid",
  "trial_request.update": "team.audit.action.trialRequest.update",
  "organization.status": "team.audit.action.organization.status",
  "organization.scheduling": "team.audit.action.organization.scheduling",
  "organization.homeowner_language": "team.audit.action.organization.homeownerLanguage",
  "organization.pipeline_switches": "team.audit.action.organization.pipelineSwitches",
  "organization.integration_switches": "team.audit.action.organization.integrationSwitches",
  "pipeline_switches.update": "team.audit.action.pipelineSwitches.update",
  "integration_switches.update": "team.audit.action.integrationSwitches.update",
  "platform_settings.update": "team.audit.action.platformSettings.update",
  "stages.update": "team.audit.action.stages.update",
  "organization.partners_access": "team.audit.action.organization.partnersAccess",
  "organization.plan": "team.audit.action.organization.plan",
  "plan.update": "team.audit.action.plan.update",
  "billing.plan": "team.audit.action.billing.plan",
  "billing.checkout": "team.audit.action.billing.checkout",
  "billing.seats": "team.audit.action.billing.seats",
  "billing.seats_checkout": "team.audit.action.billing.seatsCheckout",
  "billing.checkout_expired": "team.audit.action.billing.checkoutExpired",
  "billing.duplicate_refunded": "team.audit.action.billing.duplicateRefunded",
  "coupon.over_limit": "team.audit.action.coupon.overLimit",
  "payment.replaced": "team.audit.action.payment.replaced",
  "billing.comped": "team.audit.action.billing.comped",
  "coupon.create": "team.audit.action.coupon.create",
  "coupon.update": "team.audit.action.coupon.update",
  "coupon.grant": "team.audit.action.coupon.grant",
  "organization.switch_request": "team.audit.action.organization.switchRequest",
  "organization.switch_decision": "team.audit.action.organization.switchDecision",
  "migration.connected": "team.audit.action.migration.connected",
  "migration.preview": "team.audit.action.migration.preview",
  "migration.started": "team.audit.action.migration.started",
  "migration.completed": "team.audit.action.migration.completed",
  "migration.failed": "team.audit.action.migration.failed",
  "migration.retry": "team.audit.action.migration.retry",
  "migration.equipment": "team.audit.action.migration.equipment",
  "migration.credential_deleted": "team.audit.action.migration.credentialDeleted",
  "partner.enabled": "team.audit.action.partner.enabled",
  "partner.disabled": "team.audit.action.partner.disabled",
  "partner.updated": "team.audit.action.partner.updated",
  "partner.parts_order": "team.audit.action.partner.partsOrder",
  "partner_catalog.update": "team.audit.action.partnerCatalog.update",
  "partner_catalog.reset": "team.audit.action.partnerCatalog.reset",
};

const CRM: Record<string, string> = { hubspot: "HubSpot", salesforce: "Salesforce", zapier: "Zapier" };
const CRM_VERBS = new Set(["connect", "disconnect", "settings"]);

/** The label of an action: the table above, CRM actions by provider, else the action made readable. */
export function auditLabel(t: Translate, action: string): string {
  const known = AUDIT_LABELS[action];
  if (known) return t(known);
  const crm = /^crm\.([a-z_]+)\.([a-z_]+)$/.exec(action);
  if (crm && CRM_VERBS.has(crm[2])) return t("team.audit.crm", { verb: crm[2], provider: CRM[crm[1]] ?? crm[1] });
  const words = action.replace(/[._]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** What an audit event recorded, as `auditDetails` reads it. */
export interface AuditEventLike {
  action: string;
  target?: string | null;
  data?: Record<string, unknown> | null;
}

interface Side {
  role?: string;
  status?: string;
  mode?: string;
  locale?: string | null;
  main?: string | null;
}

/** "Language your customers see": their own language, or always one language (by its own name). */
function customerLanguage(t: Translate, side: Side): string {
  return side.mode === "fixed" && side.locale ? localeInfo(side.locale).name : t("team.audit.detail.ownLanguage");
}

/**
 * The recorded data in words: who, a role or status change, the customers' language before and after, the note.
 * A change whose before and after hold neither says nothing rather than "— → —" (live test LT-96).
 */
export function auditDetails(t: Translate, e: AuditEventLike, roleName: (role: string) => string): string {
  const d = (e.data ?? {}) as Record<string, unknown>;
  const parts: string[] = [];
  const status = (s: string | undefined) => (s ? t("team.members.status", { status: s }) : "—");
  const role = (r: string | undefined) => (r ? roleName(r) : "—");
  if (typeof d.email === "string" && !e.action.startsWith("platform_admin")) parts.push(d.email);
  const from = d.from as Side | undefined;
  const to = d.to as Side | string | undefined;
  if (e.action === "organization.homeowner_language" && from && to && typeof to === "object") {
    const before = customerLanguage(t, from);
    const after = customerLanguage(t, to);
    if (before !== after) parts.push(t("team.audit.detail.change", { from: before, to: after }));
    if ((from.main ?? null) !== (to.main ?? null)) {
      const main = (code: string | null | undefined) => (code ? localeInfo(code).name : t("team.audit.detail.default"));
      parts.push(t("team.audit.detail.mainLanguage", { from: main(from.main), to: main(to.main) }));
    }
  } else if (from && to && typeof to === "object") {
    if (from.role !== to.role) parts.push(t("team.audit.detail.change", { from: role(from.role), to: role(to.role) }));
    else if (from.status || to.status) parts.push(t("team.audit.detail.change", { from: status(from.status), to: status(to.status) }));
  } else if (typeof to === "string") parts.push(t("team.audit.detail.to", { to }));
  if (typeof d.role === "string") parts.push(role(d.role));
  if (e.action === "platform_admin.act" && e.target) parts.push(e.target);
  if (e.action.startsWith("electricity_price.") && typeof d.customers === "string") parts.push(d.customers);
  if (e.action === "electricity_price.limit")
    parts.push(t("team.audit.detail.limit", { from: String(d.previous ?? t("team.audit.detail.default")), to: String(d.limit ?? t("team.audit.detail.default")) }));
  if (typeof d.note === "string" && d.note) parts.push(t("team.audit.detail.note", { note: d.note }));
  return parts.join(" · ");
}
